<template>
    <div class="ocr-form">
        <header>
            <h1 class="mb-2">{{ $l10n.__('Espace de vérification') }}</h1>
            <a class="semibold fs-3 flex-level cpointer" @click="cancel">
                <i class="icon-arrow-back mr-1"></i>
                <span>{{
                    $l10n.__('Revenir à la liste des documents en attente de vérification')
                }}</span>
            </a>
        </header>
        <!-- LOADER -->
        <preloader :display="loading" />

        <div class="flex-row jc-center ocr-form-container">
            <!-- PREVIEW -->
            <div v-if="!loading" class="preview">
                <embed
                    v-if="preview.mime === 'application/pdf'"
                    class="wp-10 block mt-4 mb-9"
                    :src="preview.url"
                    type="application/pdf"
                />
                <div v-else-if="preview.url" class="color26-bkg wp-10 block br mt-4 mb-9 py-4 px-6">
                    <img class="wp-10 shadow br" :src="preview.url" />
                </div>
            </div>

            <!-- FORM -->
            <el-form
                v-if="!loading"
                ref="form"
                class="mb-6 ml-9"
                label-position="top"
                :model="form"
                :rules="rules"
            >
                <div v-if="errorCode" class="error-block p-2 br fs-3">
                    {{ errorLabel }}
                </div>
                <div v-show="processing" class="prelative mb-6 ml-9 hp-5">
                    <preloader :display="processing" />
                </div>
                <el-collapse
                    v-show="!processing"
                    v-model="activePanels"
                    class="collapse-infos bb-0"
                >
                    <!-- Document -->
                    <el-collapse-item name="document">
                        <template #title>
                            <div class="flex-level">
                                <CompassIcon icon="obj-slsy-document" class="mr-3" size="large" />
                                <strong class="fs-4">{{ $l10n.__('Document') }}</strong>
                            </div>
                        </template>
                        <el-form-item
                            :label="$t.__('ocr.OcrVerificationForm.supplier_name')"
                            prop="supplier"
                        >
                            <autocomplete-remote-input
                                ref="supplierSelect"
                                :value="form.supplier"
                                :preset="presetSupplier"
                                :multiple="false"
                                :route="getAutocompleteRoute"
                                :placeholder="$t.__('ocr.OcrVerificationForm.enter_supplier_name')"
                                :no-data-text="$t.__('ocr.OcrVerificationForm.no_supplier_found')"
                                :query-parameters="{ types: ['supplier'] }"
                                mode="edit"
                                :enable-add-action="canCreateSupplier"
                                :limit="1"
                                @input="updateSupplier"
                            >
                                <template #addAction v-if="canCreateSupplier">
                                    <CompassButton
                                        :label="
                                            remoteSupplier.length
                                                ? $t.__('ocr.OcrVerificationForm.create_supplier', {
                                                      supplier: remoteSupplier,
                                                  })
                                                : $t.__('ocr.OcrVerificationForm.add_supplier')
                                        "
                                        type="tertiary"
                                        class="ml-1"
                                        @click="addSupplier"
                                    />
                                </template>
                            </autocomplete-remote-input>
                        </el-form-item>
                        <div class="flex-row">
                            <el-form-item
                                class="wp-3"
                                :label="$t.__('ocr.OcrVerificationForm.document_date')"
                                prop="documentDate"
                            >
                                <date-input
                                    mode="edit"
                                    :value="form.documentDate"
                                    :format="formatDate"
                                    @input="updateDate"
                                >
                                </date-input>
                            </el-form-item>
                            <el-form-item
                                class="wp-3 ml-3"
                                :label="$t.__('common.labels.expected_close_date')"
                                prop="dueDate"
                            >
                                <date-input
                                    mode="edit"
                                    :value="form.dueDate"
                                    :format="formatDate"
                                    @input="updateDueDate"
                                >
                                </date-input>
                            </el-form-item>
                        </div>
                        <el-form-item
                            id="externalDocumentName"
                            class="wp-10"
                            :label="
                                $t.__('ocr.OcrVerificationForm.external_supplier_document_number')
                            "
                            prop="externalDocumentName"
                        >
                            <el-input v-model="form.externalDocumentName"></el-input>
                        </el-form-item>
                        <div class="flex-row">
                            <!-- DEVICE INPUT -->
                            <el-form-item
                                :label="$t.__('common.labels.currency')"
                                class="wp-3"
                                prop="currency"
                            >
                                <currency-input
                                    v-model="form.currency"
                                    mode="symbol"
                                    @input="updateCurrency"
                                ></currency-input>
                                <template #error="{ error }">
                                    <div class="el-form-item__error prelative">{{ error }}</div>
                                </template>
                            </el-form-item>
                            <!-- STEP INPUT -->
                            <el-form-item
                                :label="$t.__('ocr.OcrVerificationForm.document_status')"
                                class="ml-3 wp-3"
                                prop="step"
                            >
                                <el-select v-model="form.step">
                                    <el-option
                                        v-for="step in availableStep"
                                        :key="step.value"
                                        :value="step.value"
                                        :label="step.label"
                                    >
                                    </el-option>
                                </el-select>
                            </el-form-item>
                            <!-- Smart tags -->
                            <el-form-item
                                :label="$t.__('common.labels.smart_tags')"
                                class="wp-6 ml-3"
                            >
                                <tags
                                    :linkedtype="linkedtype"
                                    :options="defaultTags"
                                    :edit="true"
                                    :standalone="false"
                                    :fetch-tags="false"
                                    @tagsUpdated="tagsUpdated"
                                >
                                    <template #title>
                                        <span></span>
                                    </template>
                                </tags>
                            </el-form-item>
                        </div>
                    </el-collapse-item>

                    <!-- NOTE AREA -->
                    <p v-if="!showNote" class="link" @click="showNote = true">
                        {{ $l10n.__('Ajouter une note sur ce document') }}
                    </p>
                    <el-form-item v-else :label="$t.__('common.labels.note')">
                        <el-input v-model="form.note" type="textarea" :rows="6"> </el-input>
                    </el-form-item>

                    <!-- Products and Services -->
                    <el-collapse-item name="products">
                        <template #title>
                            <div class="flex-level">
                                <CompassIcon icon="obj-slsy-product" class="mr-3" size="large" />
                                <strong class="fs-4">{{ $l10n.__('Produits & Services') }}</strong>
                            </div>
                        </template>
                        <div v-for="(product, index) in form.products" :key="index" class="mb-3">
                            <div class="flex-row">
                                <el-form-item
                                    :label="$t.__('common.labels.description')"
                                    class="wp-5"
                                    :prop="`products.${index}.description`"
                                    :rules="rulesLine.description"
                                >
                                    <el-input
                                        v-model="product.description"
                                        @input="updateDescription"
                                    ></el-input>
                                    <template #error="{ error }">
                                        <div class="el-form-item__error prelative">{{ error }}</div>
                                    </template>
                                </el-form-item>
                                <el-form-item
                                    :label="$t.__('common.labels.accounting_code')"
                                    class="wp-5 ml-3"
                                    :prop="`products.${index}.accountingCode`"
                                    :rules="rulesLine.accountingCode"
                                >
                                    <el-select
                                        v-model="product.accountingCode"
                                        autocomplete="off"
                                        filterable
                                        class="wp-10"
                                        :placeholder="$t.__('ocr.OcrVerificationForm.select_code')"
                                        @change="setProductDescriptionLabel(product)"
                                    >
                                        <el-option
                                            v-for="item in accountingCodes"
                                            :key="item.id"
                                            :label="`${item.code} - ${item.label}`"
                                            :value="item.id"
                                        >
                                        </el-option>
                                    </el-select>
                                </el-form-item>
                            </div>
                            <div class="flex-row">
                                <el-form-item
                                    class="wp-3 mb-1"
                                    :label="$t.__('common.labels.amount_excluding_tax')"
                                    :prop="`products.${index}.priceWithoutTax`"
                                    :rules="rulesLine.priceWithoutTax"
                                >
                                    <amount-masked-input
                                        v-model="product.priceWithoutTax"
                                        :symbol="currencySymbol"
                                        @input="
                                            hideDefaultValues();
                                            removeLineKeeper(index);
                                        "
                                    ></amount-masked-input>
                                    <template #error="{ error }">
                                        <div class="el-form-item__error prelative">{{ error }}</div>
                                    </template>
                                </el-form-item>
                                <el-form-item
                                    class="wp-3 ml-3 mb-1"
                                    :label="$t.__('ocr.OcrVerificationForm.tax_rate')"
                                    :prop="`products.${index}.tva`"
                                    :rules="rulesLine.tva"
                                >
                                    <el-select
                                        v-model.number="product.tva"
                                        autocomplete="off"
                                        filterable
                                        type="number"
                                        class="wp-10"
                                        :placeholder="$t.__('ocr.OcrVerificationForm.select_rate')"
                                    >
                                        <el-option
                                            v-for="item in tva"
                                            :key="item.id"
                                            :label="
                                                item.label
                                                    ? `${item.rate}% - ${item.label}`
                                                    : `${item.rate}%`
                                            "
                                            :value="item.id"
                                        >
                                        </el-option>
                                    </el-select>
                                </el-form-item>
                                <el-form-item
                                    class="wp-3 ml-3 mb-1"
                                    :label="$t.__('ocr.OcrVerificationForm.VAT_amount')"
                                    :prop="`products.${index}.priceTva`"
                                    :rules="rulesLine.priceTva"
                                >
                                    <amount-masked-input
                                        v-model="product.priceTva"
                                        :symbol="currencySymbol"
                                        @input="
                                            hideDefaultValues();
                                            removeLineKeeper(index);
                                        "
                                    ></amount-masked-input>
                                </el-form-item>
                                <el-form-item
                                    class="wp-3 ml-3 mb-1"
                                    :label="$t.__('common.labels.amount_including_tax')"
                                    :prop="`products.${index}.priceWithTax`"
                                    :rules="rulesLine.priceWithTax"
                                >
                                    <amount-masked-input
                                        :value="getPriceTax(product, index)"
                                        :symbol="currencySymbol"
                                        @input="
                                            hideDefaultValues();
                                            setPriceTax($event, product, index);
                                        "
                                    ></amount-masked-input>
                                    <template #error="{ error }">
                                        <div class="el-form-item__error prelative">{{ error }}</div>
                                    </template>
                                </el-form-item>
                            </div>
                            <div class="flex-row ai-center">
                                <CompassButton
                                    v-if="index === form.products.length - 1"
                                    class="b-0 pl-0"
                                    :label="$t.__('ocr.OcrVerificationForm.add_line')"
                                    type="tertiary"
                                    @click="addProduct()"
                                />
                                <div class="flex-item-right">
                                    <CompassButton
                                        v-if="index !== 0"
                                        class="b-0 p-1"
                                        :label="$t.__('common.actions.delete')"
                                        type="tertiary-destructive"
                                        @click="deleteProduct(index)"
                                    />
                                    <CompassPlainButton
                                        :label="$t.__('ocr.OcrVerificationForm.duplicate')"
                                        @click="addProduct(product)"
                                    />
                                </div>
                            </div>
                        </div>
                        <div class="flex-row">
                            <p class="fs-4 semibold mt-0">{{ $l10n.__('Total') }}</p>
                            <el-form class="flex-item-right wp-6" label-position="left">
                                <el-form-item>
                                    <div class="flex-row flex-right ai-center">
                                        <span class="gray-60">{{
                                            $l10n.__('Montant total HT')
                                        }}</span>
                                        <amount-masked-input
                                            class="w-120 ml-3"
                                            :value="getTotalWithoutTax"
                                            :disabled="true"
                                            :symbol="currencySymbol"
                                        ></amount-masked-input>
                                    </div>
                                </el-form-item>
                                <el-form-item v-for="vatAmount in getTvaRows" :key="vatAmount.id">
                                    <div class="flex-row flex-right ai-center">
                                        <span class="gray-60">{{
                                            $l10n.__('Taux et montant TVA')
                                        }}</span>
                                        <el-input
                                            class="w-120 ml-3"
                                            :disabled="true"
                                            :value="`${vatAmount.rate}%`"
                                        ></el-input>
                                        <amount-masked-input
                                            class="w-120 ml-2"
                                            :value="getTotalByTva(vatAmount.id)"
                                            :disabled="true"
                                            :symbol="currencySymbol"
                                        ></amount-masked-input>
                                    </div>
                                </el-form-item>
                                <el-form-item>
                                    <div class="flex-row flex-right ai-center">
                                        <span class="gray-60">{{
                                            $l10n.__('Montant total TTC')
                                        }}</span>
                                        <amount-masked-input
                                            class="w-120 ml-3"
                                            :value="getTotalWithTax"
                                            :disabled="true"
                                            :symbol="currencySymbol"
                                        ></amount-masked-input>
                                    </div>
                                </el-form-item>
                            </el-form>
                        </div>
                    </el-collapse-item>
                    <!-- Payment -->
                    <el-collapse-item v-if="canAddPayment" name="payment">
                        <template #title>
                            <div class="flex-level">
                                <CompassIcon icon="obj-slsy-payment" class="mr-3" size="large" />
                                <strong class="fs-4">{{ $l10n.__('Le paiement') }}</strong>
                            </div>
                        </template>
                        <div v-for="(payment, index) of form.payments" :key="index" class="mb-4">
                            <div class="flex-row">
                                <el-form-item
                                    :label="$t.__('ocr.OcrVerificationForm.payment_date')"
                                    class="wp-3"
                                    :rules="getPaymentRules(payment).date"
                                    :prop="`payments.${index}.date`"
                                >
                                    <date-input v-model="payment.date" mode="edit"></date-input>
                                </el-form-item>
                                <el-form-item
                                    :label="$t.__('ocr.OcrVerificationForm.payment_reference')"
                                    class="wp-7 ml-3"
                                    :rules="getPaymentRules(payment).reference"
                                    :prop="`payments.${index}.reference`"
                                >
                                    <el-input v-model="payment.reference"></el-input>
                                </el-form-item>
                            </div>
                            <div class="flex-row">
                                <el-form-item
                                    :label="$t.__('common.labels.amount')"
                                    class="wp-3"
                                    :rules="getPaymentRules(payment).amount"
                                    :prop="`payments.${index}.amount`"
                                >
                                    <amount-masked-input
                                        v-model="payment.amount"
                                        :symbol="currencySymbol"
                                    ></amount-masked-input>
                                </el-form-item>
                                <el-form-item
                                    :label="$t.__('common.labels.payment_method')"
                                    class="wp-7 ml-3"
                                    :rules="getPaymentRules(payment).payment_mean"
                                    :prop="`payments.${index}.payment_mean`"
                                >
                                    <el-select
                                        v-model="payment.payment_mean"
                                        autocomplete="off"
                                        class="wp-10"
                                        filterable
                                        clearable
                                        :placeholder="
                                            $t.__('ocr.OcrVerificationForm.select_payment_method')
                                        "
                                    >
                                        <el-option
                                            v-for="item in paymentTypes"
                                            :key="item.id"
                                            :label="item.label"
                                            :value="item.id"
                                        >
                                        </el-option>
                                    </el-select>
                                </el-form-item>
                            </div>

                            <div class="flex-row">
                                <CompassButton
                                    v-if="index === form.payments.length - 1"
                                    class="pl-0 b-0"
                                    :label="$t.__('ocr.OcrVerificationForm.add_payment')"
                                    type="tertiary"
                                    @click="addPayment"
                                />
                                <CompassButton
                                    v-if="index !== 0"
                                    class="flex-item-right b-0 p-1"
                                    :label="$t.__('common.actions.delete')"
                                    type="tertiary-destructive"
                                    @click="deletePayment(index)"
                                />
                            </div>
                        </div>
                    </el-collapse-item>
                </el-collapse>
                <div class="pfixed py-3 px-4 white-bkg footer">
                    <div class="mx-auto flex-row flex-right ai-center">
                        <p v-if="multipleVerify" class="flex-item-left m-0 fs-3">
                            {{ $l10n.__('Nombre de documents restant à vérifier :') }}
                            {{ ocrRows.length - 1 }}
                        </p>
                        <CompassButton
                            :label="$t.__('ocr.OcrVerificationForm.delete_document')"
                            type="tertiary-destructive"
                            @click="deleteOCR()"
                        />
                        <CompassButton
                            :label="$t.__('ocr.OcrVerificationForm.ignore_document')"
                            class="ml-3"
                            type="secondary"
                            @click="skip()"
                        />
                        <CompassButton
                            native-type="button"
                            class="ml-3"
                            :label="$t.__('common.actions.validate')"
                            type="primary"
                            @click="submit"
                        />
                    </div>
                </div>
            </el-form>
        </div>
    </div>
</template>
<script>
import DateInput from 'vcomponents/inputs/DateInput.vue';
import CurrencyInput from 'vcomponents/inputs/CurrencyInput.vue';
import AmountMaskedInput from 'vcomponents/inputs/AmountMaskedInput.vue';
import AutocompleteRemoteInput from 'vcomponents/inputs/AutocompleteRemoteInput.vue';
import LocalStorage from 'Core/commons/LocalStorage';
import { goToUrl } from '../../helpers';
import { hydrateOcr } from 'Services/Ocr/Ocr.hydrator';
import { persistOcr } from 'Services/Ocr/Ocr.persistor';
import Preloader from 'vcomponents/utilities/Preloader.vue';
import PopoverActionButton from 'vcomponents/utilities/PopoverActionButton.vue';
import { createNamespacedHelpers, mapGetters } from 'vuex';
import Ocr from 'api/ocr';
import Payments from 'api/payments';
import Accounting from 'api/accounting';
import Companies from 'api/companies';
import Tags from 'vcomponents/utilities/Tags.vue';
import OcrVerification from 'stores/ocrVerification';

const corpMapState = createNamespacedHelpers('corp').mapState;
const userMapState = createNamespacedHelpers('user').mapState;
const { mapState } = createNamespacedHelpers('system');

export default {
    components: {
        DateInput,
        CurrencyInput,
        AmountMaskedInput,
        AutocompleteRemoteInput,
        Preloader,
        PopoverActionButton,
        Tags,
    },
    props: {
        canCreateSupplier: {
            type: Boolean,
            required: true,
        },
        linkedtype: {
            type: String,
            required: true,
        },
    },
    data() {
        return {
            loading: false,
            processing: false,
            keepDefaultValues: true,
            showNote: false,
            keepDefaultLinesValues: [],
            activePanels: ['document', 'products', 'payment'],
            availableStep: [
                {
                    label: this.$l10n.__('Brouillon'),
                    value: 'draft',
                },
                {
                    label: this.$l10n.__('À régler'),
                    value: 'due',
                },
            ],
            presetSupplier: [],
            paymentTypes: [],
            accountingCodes: [],
            defaultAccountingCodes: null,
            defaultTags: [],
            tva: [],
            preview: {
                mime: null,
                url: null,
            },
            remoteSupplier: '',
            form: {
                supplier: null,
                documentDate: null,
                dueDate: null,
                externalDocumentName: '',
                currency: null,
                step: 'due',
                note: '',
                products: [
                    {
                        description: null,
                        accountingCode: null,
                        tva: null,
                        priceTva: null,
                        priceWithoutTax: null,
                        priceWithTax: null,
                    },
                ],
                payments: [
                    {
                        date: '',
                        reference: '',
                        amount: null,
                        payment_mean: null,
                    },
                ],
                tags: [],
                totalWithoutTax: null,
                totalWithTax: null,
            },
            rules: {
                supplier: [
                    {
                        required: true,
                        message: this.$l10n.__('Veuillez renseigner un fournisseur'),
                    },
                ],
                documentDate: [
                    { required: true, message: this.$l10n.__('Veuillez renseigner une date') },
                ],
                dueDate: [
                    { required: true, message: this.$l10n.__('Veuillez renseigner une date') },
                ],
                currency: [
                    { required: true, message: this.$l10n.__('Veuillez renseigner une devise') },
                ],
                step: [{ required: true, message: this.$l10n.__('Veuillez renseigner un statut') }],
                externalDocumentName: [
                    {
                        type: 'string',
                        max: 100,
                        message: this.$l10n.__('Le numéro doit faire moins de 100 caractères'),
                    },
                ],
            },
            ocrLocalStorage: new LocalStorage('ocr', 'form.verification'),
            currencySymbol: null,
            errorCode: null,
            defaultAccountingCodeCompany: null,
        };
    },
    computed: {
        ...corpMapState({
            formatDate: 'timeformat',
            numformat: 'numformat',
        }),
        ...mapState({
            allCurrencies: 'currencies',
            errorsHandler: 'errorsHandler',
        }),
        canAddPayment() {
            return this.permissions['create_payment'];
        },
        permissions() {
            return this.$store.getters['ocr/permissions'];
        },
        rulesLine() {
            return {
                description: [
                    {
                        required: true,
                        message: this.$l10n.__('Veuillez renseigner une description'),
                    },
                ],
                accountingCode: [
                    {
                        required: true,
                        message: this.$l10n.__('Veuillez renseigner un code comptable'),
                    },
                ],
                tva: [
                    {
                        required: true,
                        message: this.$l10n.__('Veuillez renseigner un taux de TVA'),
                    },
                    {
                        type: 'number',
                        message: this.$l10n.__('Veuillez renseigner un taux valide'),
                    },
                ],
                priceTva: [
                    {
                        required: true,
                        message: this.$l10n.__('Veuillez renseigner un montant de TVA'),
                    },
                    {
                        type: 'string',
                        pattern: /^\d*[\.\,]?\d*$/,
                        min: 0,
                        message: this.$l10n.__('Veuillez renseigner un nombre valide'),
                        transform(value) {
                            return value !== null ? value.toString() : null;
                        },
                    },
                ],
                priceWithoutTax: [
                    { required: true, message: this.$l10n.__('Veuillez renseigner un montant HT') },
                    {
                        type: 'string',
                        pattern: /^\d*[\.\,]?\d*$/,
                        min: 0,
                        message: this.$l10n.__('Veuillez renseigner un nombre valide'),
                        transform(value) {
                            return value !== null ? value.toString() : null;
                        },
                    },
                ],
                priceWithTax: [
                    {
                        required: true,
                        message: this.$l10n.__('Veuillez renseigner un montant TTC'),
                    },
                    {
                        type: 'string',
                        pattern: /^\d*[\.\,]?\d*$/,
                        min: 0,
                        message: this.$l10n.__('Veuillez renseigner un nombre valide'),
                        transform(value) {
                            return value !== null ? value.toString() : null;
                        },
                    },
                ],
            };
        },
        urlBackToList() {
            return this.$router.resolve({ name: 'ocr.list', params: { linkedtype: 'purInvoice' } })
                .href;
        },
        getAutocompleteRoute() {
            return this.$router.resolve({ name: 'legacy.third.autocomplete' }).href;
        },
        getTotalWithoutTax() {
            return this.keepDefaultValues
                ? this.form.totalWithoutTax
                : this.form.products.reduce(
                      (sum, { priceWithoutTax }) =>
                          sum + NumberFormat.formatToFloat(priceWithoutTax, this.numformat),
                      0,
                  );
        },
        getTotalWithTax() {
            return this.keepDefaultValues
                ? this.form.totalWithTax
                : this.form.products.reduce(
                      (sum, { priceWithTax }) =>
                          sum + NumberFormat.formatToFloat(priceWithTax, this.numformat),
                      0,
                  );
        },
        getTvaRows() {
            const tvas = [];
            this.form.products.forEach((product) => {
                const currentTva = this.tva.find((tva) => tva.id === product.tva);
                if (
                    !_.isNil(product.tva) &&
                    !tvas.filter((tva) => tva.id === currentTva.id).length
                ) {
                    tvas.push(currentTva);
                }
            });
            return tvas;
        },
        ocrRows() {
            return this.ocrLocalStorage.get()?.rows || [];
        },
        multipleVerify() {
            return this.ocrLocalStorage.get()?.multipleVerify || false;
        },
        third() {
            return window.Third;
        },
        errorLabel() {
            let sentence =
                this.errorCode === 1
                    ? this.$l10n.__(
                          'Ce document n’a pas été traité car il contient plus de 5 pages.',
                      )
                    : this.$l10n.__(
                          'Ce document n’a pas été traité en raison d’une erreur technique.',
                      );
            sentence +=
                ' ' +
                this.$l10n.__(
                    'Vous pouvez cependant renseigner votre achat manuellement ci-dessous.',
                );
            return sentence;
        },
    },
    created() {
        this.currencySymbol = this.numformat.currencysymbol;
        this.getDictionnaries();
        this.$store.registerModule('ocr', OcrVerification);
        this.$store.dispatch('ocr/init');

        EventBus.on('third:created', (params) => {
            this.getSupplier(params.thirdid);
        });
    },
    destroyed() {
        EventBus.off('third:created');
    },
    methods: {
        getPaymentRules(payment) {
            const paymentIsEmpty =
                _.isEmpty(payment.date) &&
                _.isEmpty(payment.reference) &&
                (_.isNil(payment.amount) || _.isEmpty(payment.amount)) &&
                (_.isNil(payment.payment_mean) || !_.isNumber(payment.payment_mean));
            if (paymentIsEmpty) {
                payment.date = '';
                payment.reference = '';
                payment.amount = null;
                payment.payment_mean = null;
            }
            return {
                date: [
                    {
                        required: !paymentIsEmpty,
                        message: this.$l10n.__('Veuillez renseigner une date'),
                    },
                ],
                reference: [
                    {
                        required: false,
                        message: this.$l10n.__('Veuillez renseigner une référence'),
                    },
                ],
                amount: [
                    {
                        required: !paymentIsEmpty,
                        message: this.$l10n.__('Veuillez renseigner un montant'),
                    },
                    {
                        type: 'string',
                        pattern: /^\d*[\.\,]?\d*$/,
                        min: 0,
                        message: this.$l10n.__('Veuillez renseigner un nombre valide'),
                        transform(value) {
                            return value !== null ? value.toString() : null;
                        },
                    },
                    {
                        validator: (rule, value, callback) =>
                            Number(value) <= 0 && !paymentIsEmpty
                                ? callback(
                                      this.$l10n.__('Le montant du règlement doit être positif'),
                                  )
                                : callback(),
                    },
                ],
                payment_mean: [
                    {
                        required: !paymentIsEmpty,
                        message: this.$l10n.__('Veuillez renseigner un moyen de paiement'),
                    },
                ],
            };
        },
        addProduct(product) {
            this.form.products.push(
                !_.isNil(product)
                    ? _.cloneDeep(product)
                    : {
                          description: null,
                          accountingCode:
                              this.form.supplier === null
                                  ? this.defaultAccountingCode
                                  : this.defaultAccountingCodeCompany,
                          tva: null,
                          priceTva: null,
                          priceWithoutTax: null,
                          priceWithTax: null,
                      },
            );
        },
        addPayment() {
            this.form.payments.push({
                date: '',
                reference: '',
                amount: null,
                payment_mean: null,
            });
        },
        deletePayment(index) {
            this.form.payments.splice(index, 1);
        },
        deleteProduct(index) {
            this.form.products.splice(index, 1);
        },
        getSupplier(id) {
            this.processing = true;
            Companies.getCompanyById(id, { field: ['id', 'name'] }).then((response) => {
                this.processing = false;
                this.form.supplier = response.data.id;
                this.presetSupplier.push(response.data);
                this.$nextTick().then(() => {
                    this.$refs.supplierSelect.reset();
                    this.$refs.supplierSelect.init();
                });
            });
        },
        getOcrById() {
            Ocr.getOcrById(this.$route.params.id)
                .then((response) => {
                    if (response.state === 'error') {
                        this.errorCode = response.error_code;
                    } else {
                        this.form = {
                            ...this.form,
                            ...hydrateOcr(response.response, {
                                tvaList: this.tva,
                                accountingCodes: this.accountingCodes,
                                defaultAccountingCode: this.defaultAccountingCode,
                            }),
                        };
                        this.keepDefaultLinesValues = this.form.products.map(
                            (product, index) => index,
                        );
                        this.currencySymbol = this.allCurrencies.find(
                            (currency) => this.form.currency === currency.name,
                        )?.symbol;
                        if (
                            !_.isNil(response.response.supplier) &&
                            !_.isNil(response.response.supplier.id) &&
                            !_.isNil(response.response.supplier.value)
                        ) {
                            if (
                                !this.presetSupplier.find(
                                    (supplier) => supplier.id == response.response.supplier.id,
                                )
                            ) {
                                this.presetSupplier.push({
                                    id: response.response.supplier.id,
                                    name: response.response.supplier.value,
                                });
                            }
                        } else if (
                            !_.isNil(response.response.supplier) &&
                            _.isNil(response.response.supplier.id) &&
                            !_.isNil(response.response.supplier.value)
                        ) {
                            this.remoteSupplier = response.response.supplier.value;
                        }

                        if (!_.isNil(this.form.supplier)) {
                            this.updateAccountingCodeProduct(this.form.supplier);
                        }
                    }

                    if (!_.isNil(response.preview)) {
                        this.preview = response.preview;
                    }

                    this.loading = false;
                })
                .catch((error) => {
                    this.$router.push({
                        name: 'error.not.found',
                        params: {
                            title: this.$l10n.__('Le document que vous cherchez est introuvable'),
                            buttonOptions: {
                                label: this.$l10n.__(
                                    'Revenir à la liste des documents en attente de vérification',
                                ),
                                url: this.urlBackToList,
                            },
                        },
                    });
                    this.loading = false;
                    this.errorsHandler.logException(error);
                });
        },
        getDictionnaries() {
            this.loading = true;
            Promise.all([
                Accounting.getAllAccountingCodes(
                    { limit: 100, order: 'code' },
                    { filters: { views: ['purchase'] } },
                ),
                Accounting.getTaxes({}, { filters: { is_active: true } }),
                Accounting.getDefaultAccountingPurchaseCode(),
                Companies.getCompanies(
                    {
                        limit: 10,
                        order: 'created_at',
                        direction: 'desc',
                        field: ['id', 'name'],
                    },
                    { type: 'supplier' },
                ),
                Payments.getAllPayments(),
                Ocr.getTagsPurInvoice(),
            ]).then((response) => {
                this.accountingCodes = response[0];
                this.tva = response[1].data.data.map((taxe) => {
                    taxe.rate = parseFloat(taxe.rate).toFixed(2);
                    return taxe;
                });
                this.defaultAccountingCode = +response[2];
                this.presetSupplier = response[3].data.data;
                this.paymentTypes = response[4];
                this.getOcrById();
                this.defaultTags = response[5];
            });
        },
        getTotalByTva(tvaId) {
            return this.form.products
                .filter((product) => product.tva === tvaId)
                .reduce(
                    (sum, { priceTva }) =>
                        sum + NumberFormat.formatToFloat(priceTva, this.numformat),
                    0,
                );
        },
        cancel() {
            this.ocrLocalStorage.delete();
            goToUrl(this.urlBackToList);
        },
        submit() {
            if (this.processing) {
                return;
            }
            this.processing = true;
            this.$refs['form'].validate((valid) => {
                if (!valid) {
                    this.processing = false;
                    return false;
                }
                this.processing = true;

                const formData = persistOcr(
                    {
                        ...this.form,
                        totalWithoutTax: this.getTotalWithoutTax,
                        totalWithTax: this.getTotalWithTax,
                    },
                    { currencies: this.allCurrencies, numformat: this.numformat },
                );
                Ocr.submitVerification(this.$route.params.id, formData)
                    .then((response) => {
                        const rowsRemains = this.ocrRows.filter(
                            (row) => row.id.toString() !== this.$route.params.id,
                        );
                        if (rowsRemains.length && this.multipleVerify) {
                            this.goNextOcr(rowsRemains, 0);
                        } else {
                            const isMultiple = this.multipleVerify;
                            this.ocrLocalStorage.delete();
                            this.$router.push({
                                name: 'ocr.confirmation',
                                params: {
                                    isMultiple,
                                    linkedid: response.docid,
                                    linkedtype: this.linkedtype,
                                },
                            });
                        }
                    })
                    .catch((err) => {
                        this.errorsHandler.logException(err);
                        Push.error(
                            this.$l10n.__(
                                'Une erreur est survenue. Veuillez réessayez ultérieurement.',
                            ),
                        );
                        this.processing = false;
                    });
            });
        },
        goNextOcr(rowsRemains, nextIndex) {
            const localContent = this.ocrLocalStorage.get();
            localContent.rows = rowsRemains;
            this.ocrLocalStorage.set(localContent);
            goToUrl(
                this.$router.resolve({
                    name: 'ocr.verification.form',
                    params: { id: localContent.rows[nextIndex].id },
                }).href,
            );
        },
        deleteOCR() {
            this.processing = true;
            Ocr.deleteOcr(this.$route.params.id).then(() => {
                this.processing = false;
                const rowsRemains = this.ocrRows.filter(
                    (row) => row.id.toString() !== this.$route.params.id,
                );
                if (rowsRemains.length && this.multipleVerify) {
                    this.goNextOcr(rowsRemains, 0);
                } else {
                    const isMultiple = this.multipleVerify;
                    this.ocrLocalStorage.delete();
                    goToUrl(this.urlBackToList);
                }
            });
        },
        skip() {
            this.processing = true;
            if (this.multipleVerify) {
                const currentIndex = this.ocrRows.findIndex(
                    (row) => row.id.toString() === this.$route.params.id,
                );
                const nextIndex = _.isNil(this.ocrRows[currentIndex + 1]) ? 0 : currentIndex + 1;
                this.goNextOcr(this.ocrRows, nextIndex);
            } else {
                this.ocrLocalStorage.delete();
                goToUrl(this.urlBackToList);
            }
        },
        addSupplier(e) {
            e.stopPropagation();
            this.$refs.supplierSelect.blur();
            this.third.create('supplier', 'noRedirect', null, null, null, {
                corp_name: this.remoteSupplier,
            });
        },
        updateDescription(description) {
            this.form.description = description;
        },
        updateCurrency(currency) {
            this.form.currency = currency.name;
            this.currencySymbol = currency.symbol;
        },
        updateSupplier(supplier) {
            this.updateAccountingCodeProduct(supplier);
            this.form.supplier = supplier;
        },
        updateDate(date) {
            this.form.documentDate = date;
        },
        updateDueDate(date) {
            this.form.dueDate = date;
        },
        hideDefaultValues() {
            this.keepDefaultValues = false;
        },
        setProductDescriptionLabel(product) {
            const description = !!product.accountingCode
                ? this.accountingCodes.find((code) => code.id === product.accountingCode)
                : '';
            product.description = description?.label ?? '';
        },
        getPriceTax(product, index) {
            if (this.keepLineValues(index)) {
                return product.priceWithTax;
            } else {
                const format = { ...this.numformat, decimals: ',' };
                const priceTva = this.sanitizePrice(product.priceTva)?.includes(',')
                    ? NumberFormat.formatToFloat(product.priceTva, format)
                    : product.priceTva;
                const priceWithoutTax = this.sanitizePrice(product.priceWithoutTax)?.includes(',')
                    ? NumberFormat.formatToFloat(product.priceWithoutTax, format)
                    : product.priceWithoutTax;
                const priceCalculated = +priceTva + +priceWithoutTax;
                product.priceWithTax = NumberFormat.arround(
                    priceCalculated,
                    this.numformat,
                ).toString();
                return product.priceWithTax;
            }
        },
        sanitizePrice(price) {
            if (_.isNil(price)) {
                return;
            }
            return price.toString();
        },
        keepLineValues(index) {
            return this.keepDefaultLinesValues.includes(index);
        },
        removeLineKeeper(index) {
            this.keepDefaultLinesValues = this.keepDefaultLinesValues.filter((i) => i !== index);
        },
        setPriceTax(e, product, index) {
            this.keepDefaultLinesValues.push(index);
            product.priceWithTax = e;
        },
        tagsUpdated(tags) {
            this.form.tags = tags;
        },
        updateAccountingCodeProduct(supplier) {
            Companies.getCompanyById(supplier, { field: ['accounting_purchase_code_id'] }).then(
                (response) => {
                    this.defaultAccountingCodeCompany =
                        response.data.accounting_purchase_code_id === null
                            ? this.defaultAccountingCode
                            : response.data.accounting_purchase_code_id;
                    this.form.products.forEach((product) => {
                        product.accountingCode = this.defaultAccountingCodeCompany;
                        this.setProductDescriptionLabel(product);
                    });
                },
            );
        },
    },
};
</script>

<style lang="scss">
@import '~@sellsy/styleguide/src/_variables';
.page-ocr-verification-form {
    #sellsy_app {
        background: white;
    }
}
.ocr-form {
    .el-tag {
        height: auto;
    }

    .el-select__tags-text {
        display: inline;
        white-space: break-spaces;
        word-break: break-all;
    }

    .error-block {
        background: #fde5e8;
    }

    .ocr-form-container {
        & > form,
        & > div {
            width: 688px;
        }
    }
    .collapse-infos {
        border-top: none;

        .el-collapse-item__header {
            display: flex !important;
        }

        .el-collapse-item__header {
            margin-bottom: 16px;
            border-bottom: 1px $lightgrey solid;
            border-top: none;
        }

        .el-collapse-item__wrap {
            border-bottom: none;
        }

        .icon-title {
            height: 24px;
            width: 24px;
            border-radius: 4px;
        }

        .el-date-editor .el-input__inner,
        .el-date-editor {
            width: 100%;
        }

        .is-disabled input {
            border: none;
            color: $gray-100 !important;
        }
    }

    .preview {
        embed {
            min-height: 100vh;
        }
    }

    .footer {
        bottom: 0;
        left: 50px;
        right: 0;
        box-shadow: 0 -5px 5px -5px $lightgrey;

        > div {
            max-width: 1440px;
            width: 100%;
        }
    }

    #externalDocumentName {
        width: calc(60% + 16px);
    }
}
</style>
